import React, { FC, useCallback, useMemo } from 'react';
import { IconButton, LinearProgress, Skeleton, useTheme } from '@mui/material';
import orderBy from 'lodash/orderBy';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import moment from 'moment-timezone';

import { ITimecardDetailDTO, ITimecardPeriodDTO, ITimecardSummaryDTO } from 'src/api/dtos';
import {
  DateRangeSelector,
  Drawer,
  EmptyState,
  EmptyTimecardIcon,
  HelpFilledIcon,
  InfoFilledIcon,
  SingleStackedBarChart,
  Tab,
  Tabs,
  Tooltip,
} from 'src/shared/components';
import { DATE_MONTH_FORMAT } from 'src/shared/constants';
import { DurationFormatEnums, PeriodTypeEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import {
  formatDuration,
  isDateWithinCurrentWeek,
  isDateWithinDateRange,
  mapTimecardCategoryDarkColors,
  mapTimecardCategoryLightColors,
} from 'src/shared/utils';

import { TimecardDetail } from './TimecardDetail';

interface Props {
  periodType: PeriodTypeEnums;
  open: boolean;
  loadingSummary: boolean;
  loadingDetails: boolean;
  details: ITimecardDetailDTO[];
  periods: ITimecardPeriodDTO[];
  summary: ITimecardSummaryDTO;
  onClose: () => void;
  onTabChange: (tab: PeriodTypeEnums) => void;
  onDateRangeChance: (period: ITimecardPeriodDTO) => void;
}

export const TimecardDetailsDrawer: FC<Props> = ({
  open,
  loadingSummary,
  loadingDetails,
  periodType,
  details,
  periods,
  summary,
  onClose,
  onDateRangeChance,
  onTabChange,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const mapper = theme.palette.mode === 'light' ? mapTimecardCategoryLightColors : mapTimecardCategoryDarkColors;

  const currentPeriod = useMemo(
    () => periods.find((period) => isDateWithinCurrentWeek(period.startDate)) || periods[0],
    [periods],
  );

  const formatter = useCallback(
    (period: ITimecardPeriodDTO) => {
      const today = moment();
      const { startDate, endDate } = period;

      const date = `${moment(startDate).format(DATE_MONTH_FORMAT)} - ${moment(endDate).format(DATE_MONTH_FORMAT)}`;

      if (periodType === PeriodTypeEnums.WEEKLY) {
        if (isDateWithinDateRange(today, { startDate, endDate })) {
          return `${startCase(toLower(t('global.currentWeek')))}: ${date}`;
        }

        const lastWeek = today.clone().subtract(7, 'days');

        if (isDateWithinDateRange(lastWeek, { startDate, endDate })) {
          return `${startCase(toLower(t('global.lastWeek')))}: ${date}`;
        }
      }

      if (periodType === PeriodTypeEnums.PAY_PERIOD) {
        if (isDateWithinDateRange(today, { startDate, endDate })) {
          return `${startCase(toLower(t('timecards.currentPayPeriod')))}: ${date}`;
        }

        const diff = moment(startDate).diff(endDate, 'days');
        const lastPayPeriod = today.clone().add(diff, 'days');

        if (isDateWithinDateRange(lastPayPeriod, { startDate, endDate })) {
          return `${startCase(toLower(t('timecards.lastPayPeriod')))}: ${date}`;
        }
      }

      return date;
    },
    [periodType],
  );

  return (
    <Drawer
      width={780}
      open={open}
      title={t('timecards.timecardDetails')}
      description={t('timecards.viewTheBreakdownOfHowYouGotPaidForAGivenDay')}
      onClose={onClose}
      overflow={false}
    >
      <Tabs value={periodType} onClick={onTabChange}>
        <Tab value={PeriodTypeEnums.WEEKLY} label={t('timecards.weekly')} />
        <Tab value={PeriodTypeEnums.PAY_PERIOD} label={t('timecards.payPeriod')} />
      </Tabs>
      <div className="mt-3">
        <div className="d-flex align-items-center">
          <h3 className="h2 mb-0 me-2">{formatDuration(summary.totalHours, DurationFormatEnums.T_24_HOURS)}</h3>
          <Tooltip
            placement="bottom-start"
            title={<span className="fw-medium text-capitalize">{t('timecards.totalPaidHours')}</span>}
          >
            <IconButton className="p-0" size="small" color="info">
              <InfoFilledIcon />
            </IconButton>
          </Tooltip>
        </div>
        <div className="d-flex align-items-center justify-content-start mt-2">
          {loadingSummary ? (
            <Skeleton variant="text" component="p" animation="wave" height="8px" width="80%" />
          ) : (
            <SingleStackedBarChart
              size="small"
              scaleOnHover
              width="80%"
              data={summary?.items
                .filter((item) => !item.isMemo)
                .map((item, index) => {
                  return {
                    color: mapper[index],
                    value: item.hours,
                    tooltip: (
                      <span>
                        <span className="fw-medium">{item.timeCategoryDescription}</span>{' '}
                        {formatDuration(item.hours, DurationFormatEnums.T_24_HOURS)}
                      </span>
                    ),
                  };
                })}
            />
          )}
          <Tooltip placement="top-start" title={t('timecards.quickTip')}>
            <IconButton className="ms-2 p-0" size="small" color="info">
              <HelpFilledIcon size={16} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <DateRangeSelector
        dateRange={currentPeriod}
        formatter={formatter}
        ranges={periods}
        selectable
        onDateRangeChange={onDateRangeChance}
        className="mt-4 mb-1"
      />
      <TimecardDetails details={details} loading={loadingDetails} />
    </Drawer>
  );
};

const TimecardDetails: FC<{ details: ITimecardDetailDTO[]; loading: boolean }> = ({ details, loading }) => {
  const { t } = useTranslate();

  if (loading) {
    return (
      <div className="mt-4">
        <LinearProgress color="secondary" />
      </div>
    );
  }

  if (!details.length) {
    return (
      <EmptyState
        icon={<EmptyTimecardIcon />}
        title={t('timecards.nothingToShowYet')}
        message={t('timecards.looksLikeThereAreNoTimecardDetailsToShowForNow')}
      />
    );
  }

  return (
    <div className="overflow-auto" style={{ height: 'calc(100% - 180px)' }}>
      {orderBy(details, ['date'], ['asc']).map((detail, index) => (
        <TimecardDetail key={`timecard-card-${index}`} className="my-4" detail={detail} />
      ))}
    </div>
  );
};
